/**
 * Login Card Component
 * 
 * Inline login form within the chat.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { usePublicAuth } from '../hooks/use-public-auth';

interface LoginCardProps {
  sessionId: string;
  onSwitchToSignup: () => void;
  onClose?: () => void;
}

export const LoginCard: React.FC<LoginCardProps> = ({
  sessionId,
  onSwitchToSignup,
  onClose,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { login, requestPasswordReset, isLoading, error, clearError } = usePublicAuth();

  const [authView, setAuthView] = useState<'login' | 'forgot-password'>('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailSent, setForgotEmailSent] = useState(false);
  const [forgotEmailHelper, setForgotEmailHelper] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => setForgotEmailHelper(''), [forgotEmail]);

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setValidationErrors((prev) => ({ ...prev, [field]: '' }));
    clearError();
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    await login(formData, sessionId);
  };

  const goToForgotPassword = () => {
    setAuthView('forgot-password');
    setForgotEmail(formData.email.trim());
    setForgotEmailSent(false);
    setForgotEmailHelper('');
    clearError();
  };

  const backToLogin = () => {
    setAuthView('login');
    setForgotEmailSent(false);
    setForgotEmailHelper('');
    clearError();
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotEmailSent(false);
    const result = await requestPasswordReset(forgotEmail);
    if (result === 'success') {
      setForgotEmailSent(true);
      setForgotEmailHelper('');
      return;
    }
    if (result === 'invalid_email' || result === 'email_validation') {
      setForgotEmailHelper(t('publicChat.forgotPasswordInvalidEmail'));
      return;
    }
    if (result === 'user_not_found') {
      setForgotEmailHelper(t('publicChat.forgotPasswordUserNotFound'));
    }
  };

  if (authView === 'forgot-password') {
    return (
      <Box className={classes.card}>
        <Box className={classes.header}>
          <Box className={classes.headerText}>
            <Typography variant="h6" className={classes.title}>
              {t('publicChat.forgotPasswordTitle')}
            </Typography>
            {!forgotEmailSent ? (
              <Typography variant="body2" className={classes.forgotLead}>
                {t('publicChat.forgotPasswordLead')}
              </Typography>
            ) : null}
          </Box>
          {onClose && (
            <IconButton onClick={onClose} size="small" className={classes.closeButton}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {!forgotEmailSent ? (
          <form onSubmit={handleForgotSubmit} className={classes.form}>
            <TextField
              label={t('publicChat.email', 'Email')}
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              error={forgotEmailHelper !== ''}
              helperText={forgotEmailHelper}
              size="small"
              fullWidth
              className={classes.textField}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isLoading || forgotEmail.trim() === '' || forgotEmailHelper !== ''}
              className={classes.submitButton}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('publicChat.forgotPasswordButton')
              )}
            </Button>
          </form>
        ) : (
          <Box className={classes.forgotSuccess}>
            <CheckCircleOutlineIcon className={classes.forgotSuccessIcon} color="success" fontSize="large" />
            <Box className={classes.forgotSuccessText}>
              <Typography variant="subtitle2" className={classes.forgotSuccessTitle}>
                {t('publicChat.forgotPasswordSuccessTitle')}
              </Typography>
              <Typography variant="body2" className={classes.forgotSuccessBody}>
                {t('publicChat.forgotPasswordSuccessBody', { email: forgotEmail })}
              </Typography>
            </Box>
          </Box>
        )}

        <Box className={classes.forgotFooter}>
          <Button variant="text" size="small" onClick={backToLogin} className={classes.switchButton}>
            {t('publicChat.backToLogin')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.card}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h6" className={classes.title}>
            {t('publicChat.loginTitle', 'Welcome Back')}
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            {t('publicChat.loginSubtitle', 'Log in to continue')}
          </Typography>
        </Box>
        {onClose && (
          <IconButton onClick={onClose} size="small" className={classes.closeButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" className={classes.alert}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className={classes.form}>
        <TextField
          label={t('publicChat.email', 'Email')}
          type="email"
          value={formData.email}
          onChange={handleChange('email')}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          size="small"
          fullWidth
          className={classes.textField}
        />

        <TextField
          label={t('publicChat.password', 'Password')}
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleChange('password')}
          error={!!validationErrors.password}
          helperText={validationErrors.password}
          size="small"
          fullWidth
          className={classes.textField}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  size="small"
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box className={classes.forgotRow}>
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={goToForgotPassword}
            className={classes.forgotLink}
          >
            {t('publicChat.forgotPasswordLink', t('forgotpw'))}
          </Button>
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading}
          className={classes.submitButton}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('publicChat.loginButton', 'Log In')
          )}
        </Button>
      </form>

      {/* Switch to signup */}
      <Box className={classes.switchAuth}>
        <Typography variant="body2" className={classes.switchText}>
          {t('publicChat.noAccount', 'Don\'t have an account?')}
        </Typography>
        <Button
          variant="text"
          size="small"
          onClick={onSwitchToSignup}
          className={classes.switchButton}
        >
          {t('publicChat.signupInstead', 'Sign up free')}
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.greys.lighter}`,
    padding: theme.spacing(3),
    maxWidth: 420,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  headerText: {
    flex: 1,
    minWidth: 0,
    paddingRight: theme.spacing(1),
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  closeButton: {
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  textField: {},
  forgotRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    marginTop: theme.spacing(-0.5),
  },
  forgotLink: {
    color: theme.palette.primary.main,
    textTransform: 'none',
    padding: theme.spacing(0.25, 0.5),
    minWidth: 'auto',
    fontSize: '0.8125rem',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}14`,
    },
  },
  forgotLead: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
    lineHeight: 1.5,
    fontSize: '0.875rem',
  },
  forgotSuccess: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(46, 125, 50, 0.12)' : 'rgba(46, 125, 50, 0.08)',
    border: `1px solid ${theme.palette.success.main}33`,
    borderLeftWidth: 4,
    borderLeftColor: theme.palette.success.main,
  },
  forgotSuccessIcon: {
    flexShrink: 0,
    marginTop: 2,
  },
  forgotSuccessText: {
    flex: 1,
    minWidth: 0,
  },
  forgotSuccessTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.75),
  },
  forgotSuccessBody: {
    color: theme.palette.text.secondary,
    lineHeight: 1.55,
    wordBreak: 'break-word',
  },
  forgotFooter: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
  },
  submitButton: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    padding: theme.spacing(1.5),
    borderRadius: 10,
    textTransform: 'none',
    marginTop: theme.spacing(1),
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
    },
  },
  switchAuth: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(2),
  },
  switchText: {
    color: theme.palette.text.secondary,
  },
  switchButton: {
    color: theme.palette.primary.main,
    textTransform: 'none',
    padding: theme.spacing(0.25, 0.5),
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}14`,
    },
  },
}));

export default LoginCard;

